/**
 * Reset-password screen — email + OTP + new_password, POST
 * /auth/password/reset/confirm.
 *
 * CONTRACT (unchanged — ResetPasswordScreen.test.tsx covers it):
 *   - Fields: email, otp (6 digits), new_password (12+).
 *   - On 200: success toast, then /login.
 *   - On 422 `password.breached` / `password.too_short`: inline error on the
 *     new-password field (mirrors signup's mapping).
 *   - On 400 `auth.password.reset.invalid_or_expired`: inline error at the
 *     code, cells cleared so the user retypes.
 *
 * ── The counter rebuild (2026-08) ────────────────────────────────────────────
 *
 * Three fields with three different shapes (a boxed input, a row of loose
 * cells, another boxed input) used to read as three unrelated asks. They are
 * now three entries of one ruled slip, and the code sits in the same recessed
 * well as on /verify-email so a user arriving from the email recognises it.
 *
 * FOCUS ON FAILURE. react-hook-form focuses the first invalid registered field
 * on a failed submit, but the OTP is a controlled component with no registered
 * ref — so a form whose ONLY problem was the code used to report an error and
 * leave the caret nowhere. `OtpInput` now exposes `focusFirst()` and the
 * invalid-submit handler calls it when the code is the first thing wrong.
 *
 * The submit button is no longer disabled until six digits exist: validation
 * explains the gap instead of the control silently refusing to work.
 *
 * The success path uses `notify.success`, not a bare `toast()` — the shared
 * layer gives the confirmation an icon so it never depends on colour alone.
 */
import { zodResolver } from "@hookform/resolvers/zod";
import { OctagonAlert } from "lucide-react";
import { useRef, useState, type ReactElement } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import { Stagger, StaggerItem } from "@/components/motion";
import { AuthCounter } from "@/features/auth/components/AuthCounter";
import {
  AUTH_LINK_CLASS,
  AuthLink,
  AuthSubmit,
} from "@/features/auth/components/controls";
import {
  OtpInput,
  type OtpInputHandle,
} from "@/features/auth/components/OtpInput";
import { PasswordField } from "@/features/auth/components/PasswordField";
import {
  SLIP_INPUT_CLASS,
  Slip,
  SlipField,
  SlipNotice,
  SlipRow,
} from "@/features/auth/components/slip";
import { withReturnTo } from "@/features/auth/destination";
import {
  MIN_PASSWORD_LENGTH,
  resetPasswordDefaultValues,
  resetPasswordSchema,
  type ResetPasswordFormValues,
} from "@/features/auth/schemas";
import { ApiError, NetworkError, apiClient, AUTH_PATHS } from "@/lib/api-client";
import { iconProps } from "@/lib/icons";
import { notify } from "@/lib/notify";

interface PasswordResetConfirmResponse {
  message_key: "auth.password.reset.success";
}

function mapResetError(err: unknown): {
  field: "newPassword" | "otp" | "root";
  key: string;
  retryable: boolean;
} {
  if (err instanceof NetworkError) {
    return { field: "root", key: "errors.network", retryable: true };
  }
  if (!(err instanceof ApiError)) {
    return { field: "root", key: "errors.generic", retryable: true };
  }
  switch (err.code) {
    case "password.breached":
      return {
        field: "newPassword",
        key: "errors.auth.password.breached",
        retryable: false,
      };
    case "password.too_short":
      return {
        field: "newPassword",
        key: "errors.auth.password.too_short",
        retryable: false,
      };
    case "password.hibp_unavailable":
      return {
        field: "root",
        key: "errors.auth.password.hibp_unavailable",
        retryable: true,
      };
    case "auth.password.reset.invalid_or_expired":
      return {
        field: "otp",
        key: "errors.auth.password.reset.invalid_or_expired",
        retryable: false,
      };
    default:
      return { field: "root", key: "errors.generic", retryable: true };
  }
}

export function ResetPasswordScreen(): ReactElement {
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rawReturnTo = searchParams.get("return_to");
  const [serverError, setServerError] = useState<{
    key: string;
    retryable: boolean;
  } | null>(null);
  const otpRef = useRef<OtpInputHandle | null>(null);

  const form = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: resetPasswordDefaultValues,
    mode: "onBlur",
  });

  const onSubmit = async (values: ResetPasswordFormValues): Promise<void> => {
    setServerError(null);
    try {
      await apiClient.post<
        PasswordResetConfirmResponse,
        { email: string; otp: string; new_password: string }
      >(
        AUTH_PATHS.passwordResetConfirm,
        {
          email: values.email,
          otp: values.otp,
          new_password: values.newPassword,
        },
        { skipRefreshOn401: true },
      );
      notify.success(t("resetPassword.successToast"));
      navigate(withReturnTo("/login", rawReturnTo), { replace: true });
    } catch (err) {
      const mapped = mapResetError(err);
      if (mapped.field === "newPassword") {
        form.setError("newPassword", { message: mapped.key });
        form.setFocus("newPassword");
      } else if (mapped.field === "otp") {
        form.setError("otp", { message: mapped.key });
        // Clear the cells so the user retypes (mirrors verify-email), then put
        // the caret in the first one — RHF cannot, the field is controlled.
        form.setValue("otp", "");
        otpRef.current?.focusFirst();
      } else {
        setServerError({ key: mapped.key, retryable: mapped.retryable });
      }
    }
  };

  /**
   * Submit-time validation failure. RHF's `shouldFocusError` handles `email`
   * and `newPassword` (both registered); the code is controlled, so when it is
   * the first — or only — invalid field we move focus by hand.
   */
  const onInvalid = (): void => {
    const errs = form.formState.errors;
    if (errs.email === undefined && errs.otp !== undefined) {
      otpRef.current?.focusFirst();
    }
  };

  const submit = form.handleSubmit(onSubmit, onInvalid);
  const isSubmitting = form.formState.isSubmitting;
  const emailErrorKey = form.formState.errors.email?.message;
  const otpErrorKey = form.formState.errors.otp?.message;
  const newPasswordErrorKey = form.formState.errors.newPassword?.message;
  const otpValue = form.watch("otp") ?? "";
  const newPasswordValue = form.watch("newPassword") ?? "";

  return (
    <AuthCounter
      door="consumer"
      doorName={t("resetPassword.doorName")}
      title={t("resetPassword.title")}
      lede={t("resetPassword.lede")}
      testId="reset-password-screen"
    >
      <Stagger className="gav2-stack-lg flex flex-col">
        <StaggerItem>
          <form
            className="gav2-stack-md flex flex-col"
            onSubmit={submit}
            noValidate
            aria-describedby={serverError !== null ? "reset-server-error" : undefined}
          >
            <Slip>
              <SlipField
                htmlFor="reset-email"
                label={t("resetPassword.emailLabel")}
                required
                requiredLabel={t("common.requiredField")}
                error={emailErrorKey !== undefined ? t(emailErrorKey) : undefined}
                errorId="reset-email-error"
                errorTestId="reset-email-error"
              >
                <input
                  id="reset-email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  autoFocus
                  aria-required="true"
                  aria-invalid={emailErrorKey !== undefined || undefined}
                  aria-describedby={
                    emailErrorKey !== undefined ? "reset-email-error" : undefined
                  }
                  placeholder={t("resetPassword.emailPlaceholder")}
                  disabled={isSubmitting}
                  data-testid="reset-email"
                  className={SLIP_INPUT_CLASS}
                  {...form.register("email")}
                />
              </SlipField>

              {/* The code is its own entry of the slip, not a floating row of
                  boxes: same rules, same padding, same label grid. */}
              <SlipRow invalid={otpErrorKey !== undefined}>
                <OtpInput
                  ref={otpRef}
                  value={otpValue}
                  onChange={(next) => {
                    form.setValue("otp", next, { shouldDirty: true });
                    if (otpErrorKey !== undefined) {
                      form.clearErrors("otp");
                    }
                  }}
                  label={t("resetPassword.otpLabel")}
                  required
                  disabled={isSubmitting}
                  invalid={otpErrorKey !== undefined}
                  errorId={otpErrorKey !== undefined ? "reset-otp-error" : undefined}
                  autoFocus={false}
                  testId="reset-otp-cell"
                />
                {otpErrorKey !== undefined ? (
                  <p
                    id="reset-otp-error"
                    role="alert"
                    className="mt-1 text-[0.75rem] font-medium leading-snug text-[var(--g-error)]"
                    data-testid="reset-otp-error"
                  >
                    {t(otpErrorKey)}
                  </p>
                ) : null}
              </SlipRow>

              <PasswordField
                id="reset-new-password"
                label={t("resetPassword.newPasswordLabel")}
                autoComplete="new-password"
                placeholder={t("resetPassword.newPasswordPlaceholder")}
                disabled={isSubmitting}
                data-testid="reset-new-password"
                hint={t("resetPassword.newPasswordHint")}
                hintId="reset-new-password-hint"
                aria-describedby={
                  newPasswordErrorKey !== undefined
                    ? "reset-new-password-error reset-new-password-hint"
                    : "reset-new-password-hint"
                }
                error={
                  newPasswordErrorKey !== undefined ? t(newPasswordErrorKey) : undefined
                }
                errorId="reset-new-password-error"
                errorTestId="reset-new-password-error"
                valueLength={newPasswordValue.length}
                minLength={MIN_PASSWORD_LENGTH}
                {...form.register("newPassword")}
              />
            </Slip>

            {serverError !== null ? (
              <SlipNotice
                tone="error"
                role="alert"
                id="reset-server-error"
                icon={<OctagonAlert {...iconProps("sm")} />}
              >
                <p data-testid="reset-server-error">{t(serverError.key)}</p>
                {serverError.retryable ? (
                  <button
                    type="button"
                    onClick={() => void submit()}
                    disabled={isSubmitting}
                    data-testid="reset-error-retry"
                    className={"mt-1 " + AUTH_LINK_CLASS}
                  >
                    {t("common.retry")}
                  </button>
                ) : null}
              </SlipNotice>
            ) : null}

            <AuthSubmit
              pending={isSubmitting}
              pendingLabel={t("resetPassword.submitting")}
              testId="reset-submit"
            >
              {t("resetPassword.submit")}
            </AuthSubmit>
          </form>
        </StaggerItem>

        <StaggerItem>
          <AuthLink
            to={withReturnTo("/login", rawReturnTo)}
            testId="reset-back-link"
            tone="muted"
          >
            {t("resetPassword.backToLogin")}
          </AuthLink>
        </StaggerItem>
      </Stagger>
    </AuthCounter>
  );
}

export default ResetPasswordScreen;
